import { useState, useEffect, useCallback } from 'react';
import type { CloudType } from '../types/api';
import {
  fetchGcpProjectIds,
  fetchAwsAccounts,
  fetchAzureSubscriptions,
  AwsAccount,
  AzureSubscription
} from '../services/cloudApi';

export interface AwsAccountInfo {
  accountId: string;
  accountName: string;
}

export interface AzureSubscriptionInfo {
  subscriptionId: string;
  subscriptionName: string;
}

interface CloudAccounts {
  gcp: string[];
  aws: AwsAccountInfo[];
  azure: AzureSubscriptionInfo[];
}

interface SelectedCloud {
  cloudType: CloudType | null;
  cloudId: string | null;
}

// LocalStorage keys (matching your existing app)
const STORAGE_KEYS = {
  GCP_PROJECT_ID: 'gcpSelectedProjectId',
  AWS_ACCOUNT_ID: 'awsSelectedAccountId',
  AZURE_SUBSCRIPTION_ID: 'azureSelectedSubscriptionId',
  LATEST_TENANT: 'latestTenant',
};

const lsGetValue = (key: string): any => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'value' in parsed) return parsed.value ?? null;
  } catch { }
  return raw;
};

const lsSetValue = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify({ value }));
};

export function useCloudAccounts() {
  const [accounts, setAccounts] = useState<CloudAccounts>({
    gcp: [],
    aws: [],
    azure: [],
  });
  const [selectedCloud, setSelectedCloud] = useState<SelectedCloud>({
    cloudType: null,
    cloudId: null,
  });
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  // Get tenant name from localStorage using consistent {"value": ...} format
  const getTenantName = useCallback((): string | null => {
    const tenantData = lsGetValue(STORAGE_KEYS.LATEST_TENANT);
    if (tenantData && typeof tenantData === 'object') {
      return tenantData.tenantName || null;
    }
    return null;
  }, []);

  // Fetch all cloud account IDs
  const fetchAllAccounts = useCallback(async () => {
    const tenantName = getTenantName();

    if (!tenantName) {
      console.warn('No tenant name found in localStorage');
      setAccounts({ gcp: [], aws: [], azure: [] });
      setIsLoadingAccounts(false);
      return;
    }

    setIsLoadingAccounts(true);

    try {
      // Fetch all three in parallel using the cloudApi service
      const [gcpRes, awsRes, azureRes] = await Promise.allSettled([
        fetchGcpProjectIds(tenantName),
        fetchAwsAccounts(tenantName),
        fetchAzureSubscriptions(tenantName),
      ]);

      // Process GCP - returns string[] directly
      const gcpIds = gcpRes.status === 'fulfilled' ? gcpRes.value : [];

      // Process AWS - returns AwsAccount[], keep full info
      const awsRaw = awsRes.status === 'fulfilled' ? awsRes.value : [];
      const awsAccounts: AwsAccountInfo[] = awsRaw.map((item: AwsAccount) => ({
        accountId: item.accountId,
        accountName: item.accountName || item.accountId,
      }));

      // Process Azure - returns AzureSubscription[], keep full info
      const azureRaw = azureRes.status === 'fulfilled' ? azureRes.value : [];
      const azureSubscriptions: AzureSubscriptionInfo[] = azureRaw
        .filter((item: AzureSubscription) => item.azureSubscriptionId)
        .map((item: AzureSubscription) => ({
          subscriptionId: item.azureSubscriptionId,
          subscriptionName: item.subscriptionName || item.azureSubscriptionId,
        }));

      setAccounts({
        gcp: gcpIds,
        aws: awsAccounts,
        azure: azureSubscriptions,
      });
    } catch (error) {
      console.error('Failed to fetch cloud accounts:', error);
      setAccounts({ gcp: [], aws: [], azure: [] });
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [getTenantName]);

  // Load saved selection from localStorage using consistent {"value": ...} format
  const loadSavedSelection = useCallback(() => {
    const gcpId = lsGetValue(STORAGE_KEYS.GCP_PROJECT_ID);
    const awsId = lsGetValue(STORAGE_KEYS.AWS_ACCOUNT_ID);
    const azureId = lsGetValue(STORAGE_KEYS.AZURE_SUBSCRIPTION_ID);

    // Determine which cloud is currently selected based on saved IDs
    // Priority: check most recently set one (we'll check all and take the first valid)
    if (gcpId && typeof gcpId === 'string' && gcpId.length > 0) {
      setSelectedCloud({ cloudType: 'GCP', cloudId: gcpId });
      return;
    }
    if (awsId && typeof awsId === 'string' && awsId.length > 0) {
      setSelectedCloud({ cloudType: 'AWS', cloudId: awsId });
      return;
    }
    if (azureId && typeof azureId === 'string' && azureId.length > 0) {
      setSelectedCloud({ cloudType: 'AZURE', cloudId: azureId });
      return;
    }
  }, []);

  // Select a cloud account using consistent {"value": ...} format
  const selectCloudAccount = useCallback((cloudType: CloudType, cloudId: string) => {
    // Clear all selections first
    lsSetValue(STORAGE_KEYS.GCP_PROJECT_ID, '');
    lsSetValue(STORAGE_KEYS.AWS_ACCOUNT_ID, '');
    lsSetValue(STORAGE_KEYS.AZURE_SUBSCRIPTION_ID, '');

    // Set the new selection
    if (cloudType === 'GCP') {
      lsSetValue(STORAGE_KEYS.GCP_PROJECT_ID, cloudId);
    } else if (cloudType === 'AWS') {
      lsSetValue(STORAGE_KEYS.AWS_ACCOUNT_ID, cloudId);
    } else if (cloudType === 'AZURE') {
      lsSetValue(STORAGE_KEYS.AZURE_SUBSCRIPTION_ID, cloudId);
    }

    setSelectedCloud({ cloudType, cloudId });

    // Keep multiple hook instances in sync within the same tab
    window.dispatchEvent(new CustomEvent('cloud-selection-changed'));
  }, []);

  // Helper to get display name for current selection (must be before useEffects)
  const getDisplayName = useCallback((cloudType: CloudType | null, cloudId: string | null): string => {
    if (!cloudType || !cloudId) return '';

    if (cloudType === 'AWS') {
      const account = accounts.aws.find(a => a.accountId === cloudId);
      return account?.accountName || cloudId;
    }
    if (cloudType === 'AZURE') {
      const subscription = accounts.azure.find(a => a.subscriptionId === cloudId);
      return subscription?.subscriptionName || cloudId;
    }
    return cloudId; // GCP uses project ID as display
  }, [accounts]);

  // Initialize on mount
  useEffect(() => {
    loadSavedSelection();
    fetchAllAccounts();
  }, [loadSavedSelection, fetchAllAccounts]);

  // Sync selection across multiple useCloudAccounts() hook instances (same tab)
  useEffect(() => {
    const handleSelectionChanged = () => loadSavedSelection();

    window.addEventListener('cloud-selection-changed', handleSelectionChanged);
    window.addEventListener('storage', handleSelectionChanged);

    return () => {
      window.removeEventListener('cloud-selection-changed', handleSelectionChanged);
      window.removeEventListener('storage', handleSelectionChanged);
    };
  }, [loadSavedSelection]);

  return {
    accounts,
    selectedCloud,
    isLoadingAccounts,
    isSwitching,
    setIsSwitching,
    selectCloudAccount,
    refetchAccounts: fetchAllAccounts,
    getTenantName,
    getDisplayName,
  };
}
